import { useState, useCallback, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'js-cookie';
import { CheckIcon, CheckCircleIcon } from '@heroicons/react/outline';
import { toast } from 'react-toastify';

const SellerRegistrationForm = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [otpSent, setOtpSent] = useState(false);
  const [files, setFiles] = useState([]);
  const [filePreviews, setFilePreviews] = useState([]);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [token, setToken] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [otp,setOtp] = useState('')
  useEffect(() => {
    const userToken = Cookies.get("ecom_user_token");
    if (userToken) {
      setToken(userToken);
    } else {
      console.error("No authentication token found");
    }
  }, []);

  const [formData, setFormData] = useState({
    phone: '',
    otp: '',
    businessName: '',
    email: '',
    panNumber: '',
    gstNumber: '',
    pickupPinCode: '',
    pickupAddress: '',
    pickupCity: '',
    pickupState: '',
    accountHolderName: '',
    bankName: '',
    accountNumber: '',
    ifscCode: '',
    password: '',
    confirmPassword: ''
  });

  const api = axios.create({
    baseURL: process.env.REACT_APP_BACKEND_URL,
    headers: {
      'Content-Type': 'application/json',
      Authorization: token ? `Bearer ${token}` : ''
    }
  });

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors]);

  const handlePincodeChange = async (e) => {
    const pincode = e.target.value;
    setFormData(prev => ({ ...prev, pickupPinCode: pincode }));
    
    if (/^\d{6}$/.test(pincode)) {
      try {
        const response = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
        const data = await response.json();
        if (data[0]?.Status === "Success" && data[0]?.PostOffice?.[0]) {
          const postOffice = data[0].PostOffice[0];
          setFormData(prev => ({
            ...prev,
            pickupCity: postOffice.District || postOffice.Block || '',
            pickupState: postOffice.State || ''
          }));
        }
      } catch (error) {
        console.error("Pincode lookup failed:", error);
      }
    }
  };

  const handleFileChange = useCallback((e) => {
    const MAX_FILE_SIZE = 5 * 1024 * 1024;
    const MAX_TOTAL_FILES = 5;
    
    const newFiles = Array.from(e.target.files).filter(file => {
      if (file.size > MAX_FILE_SIZE) {
        setErrors(prev => ({
          ...prev,
          documents: `File ${file.name} exceeds 5MB limit`
        }));
        return false;
      }
      return true;
    });
    
    if (newFiles.length + files.length > MAX_TOTAL_FILES) {
      setErrors(prev => ({
        ...prev,
        documents: 'Maximum 5 documents allowed'
      }));
      return;
    }
    
    setFiles(prev => [...prev, ...newFiles]);
    
    const previews = newFiles.map(file => ({
      name: file.name,
      size: file.size,
      type: file.type,
      url: URL.createObjectURL(file)
    }));
    
    setFilePreviews(prev => [...prev, ...previews]);
    
    if (errors.documents) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.documents;
        return newErrors;
      });
    }
  }, [files, errors.documents]);

  const removeFile = useCallback((index) => {
    if (filePreviews[index]?.url) {
      URL.revokeObjectURL(filePreviews[index].url);
    }
    
    const newFiles = [...files];
    newFiles.splice(index, 1);
    setFiles(newFiles);
    
    const newPreviews = [...filePreviews];
    newPreviews.splice(index, 1);
    setFilePreviews(newPreviews);
  }, [files, filePreviews]);

  const validationPatterns = {
    phone: /^\d{10}$/,
    otp: /^\d{6}$/,
    email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    panNumber: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
    gstNumber: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
    pickupPinCode: /^[1-9][0-9]{5}$/,
    accountNumber: /^\d{9,18}$/,
    ifscCode: /^[A-Z]{4}0[A-Z0-9]{6}$/,
    password: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/
  };

  const PasswordStrengthMeter = ({ password }) => {
    const getStrength = (pass) => {
      if (!pass) return 0;
      let strength = 0;
      if (pass.length >= 8) strength++;
      if (/[A-Z]/.test(pass)) strength++;
      if (/[a-z]/.test(pass)) strength++;
      if (/[0-9]/.test(pass)) strength++;
      if (/[^A-Za-z0-9]/.test(pass)) strength++;
      return strength;
    };

    const strength = getStrength(password);
    const strengthText = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'][strength];
    const strengthColor = ['red', 'orange', 'yellow', 'blue', 'green'][strength];

    return (
      <div className="mt-1">
        <div className="flex items-center">
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className={`h-1 flex-1 mx-0.5 rounded ${i <= strength ? `bg-${strengthColor}-500` : 'bg-gray-200'}`}
            />
          ))}
        </div>
        <p className={`text-xs mt-1 text-${strengthColor}-600`}>
          Password Strength: {strengthText}
        </p>
      </div>
    );
  };

  const validateStep = useCallback(() => {
    const newErrors = {};
    const requiredFields = {
      1: otpSent ? ['phone', 'otp'] : ['phone'],
      2: ['businessName'],
      3: ['email'],
      4: ['panNumber', 'gstNumber'],
      5: ['pickupPinCode', 'pickupAddress', 'pickupCity', 'pickupState'],
      6: ['accountHolderName', 'bankName', 'accountNumber', 'ifscCode'],
      7: ['password', 'confirmPassword']
    };

    requiredFields[step].forEach(field => {
      if (!formData[field]?.trim()) {
        newErrors[field] = 'This field is required';
      }
    });

    switch(step) {
      case 1:
        if (!validationPatterns.phone.test(formData.phone)) {
          newErrors.phone = 'Please enter a valid 10-digit phone number';
        }
        if (otpSent && !validationPatterns.otp.test(formData.otp)) {
          newErrors.otp = 'Please enter a valid 6-digit OTP';
        }
        break;
      case 3:
        if (!validationPatterns.email.test(formData.email)) {
          newErrors.email = 'Please enter a valid email address';
        }
        break;
      case 4:
        if (!validationPatterns.panNumber.test(formData.panNumber)) {
          newErrors.panNumber = 'Please enter a valid PAN number (e.g., ABCDE1234F)';
        }
        if (!validationPatterns.gstNumber.test(formData.gstNumber)) {
          newErrors.gstNumber = 'Please enter a valid GST number (e.g., 22ABCDE1234F1Z5)';
        }
        break;
      case 5:
        if (!validationPatterns.pickupPinCode.test(formData.pickupPinCode)) {
          newErrors.pickupPinCode = 'Please enter a valid 6-digit pincode';
        }
        break;
      case 6:
        if (!validationPatterns.accountNumber.test(formData.accountNumber)) {
          newErrors.accountNumber = 'Please enter a valid account number (9-18 digits)';
        }
        if (!validationPatterns.ifscCode.test(formData.ifscCode)) {
          newErrors.ifscCode = 'Please enter a valid IFSC code (e.g., ABCD0123456)';
        }
        break;
      case 7:
        if (!validationPatterns.password.test(formData.password)) {
          newErrors.password = 'Password must contain: uppercase, lowercase, number, and special character';
        }
        if (formData.password !== formData.confirmPassword) {
          newErrors.confirmPassword = 'Passwords do not match';
        }
        if (files.length === 0) {
          newErrors.documents = 'Please upload required documents for verification';
        }
        if (!acceptedTerms) {
          newErrors.terms = 'You must accept the terms and conditions';
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [step, formData, otpSent, files, acceptedTerms]);

  const sendOtp = async () => {
    if (!validationPatterns.phone.test(formData.phone)) {
      setErrors({ phone: 'Please enter a valid 10-digit phone number' });
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post('/api/request/seller/sendSellerOtp', { 
        phone: formData.phone 
      });

      setOtp(response.data.otp)
      
      if (response.data.message === 'OTP sent successfully.') {
        setOtpSent(true);
      } else {
        setErrors({ phone: response.data.message || 'Failed to send OTP' });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                         error.response?.data?.error || 
                         'Failed to send OTP. Please try again.';
      setErrors({ phone: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (!validationPatterns.otp.test(formData.otp)) {
      setErrors({ otp: 'Please enter a valid 6-digit OTP' });
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post('/api/request/seller/verifySellerOtp', {
        phone: formData.phone,
        otp: formData.otp
      });
      
      if (response.status === 200 || response.data.status === 201) {
        setStep(2);
      } else {
        setErrors({ otp: response.data.message || 'OTP verification failed' });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                         error.response?.data?.error || 
                         'OTP verification failed. Please try again.';
      setErrors({ otp: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const submitForm = async () => {
    if (!validateStep()) return;
    
    setLoading(true);
    try {
      const formDataToSend = new FormData();

      files.forEach((file) => {
        formDataToSend.append('documents', file);
      });

      formDataToSend.append('businessName', formData.businessName);
      formDataToSend.append('panNumber', formData.panNumber);
      formDataToSend.append('gstNumber', formData.gstNumber);
      formDataToSend.append('password', formData.password);

      formDataToSend.append('bankDetails[accountHolderName]', formData.accountHolderName);
      formDataToSend.append('bankDetails[bankName]', formData.bankName);
      formDataToSend.append('bankDetails[accountNumber]', formData.accountNumber);
      formDataToSend.append('bankDetails[ifscCode]', formData.ifscCode);
      formDataToSend.append('contactDetails[phone]', formData.phone);
      formDataToSend.append('contactDetails[email]', formData.email);
      
      formDataToSend.append('address[pickupPinCode]', formData.pickupPinCode);
      formDataToSend.append('address[pickupAddress]', formData.pickupAddress);
      formDataToSend.append('address[pickupCity]', formData.pickupCity);
      formDataToSend.append('address[pickupState]', formData.pickupState);

      const response = await api.post('/api/request/seller/submit', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.message === 'Seller request submitted successfully.') {
        setShowSuccessModal(true);
      } else {
        setErrors({ 
          form: response.data.message || 'Registration failed',
          ...(response.data.errors || {})
        });
      }
    } catch (error) {
      console.error('Submission error:', error);
      
      if (error.response) {
        const errorData = error.response.data;
        setErrors({
          form: errorData.message || 'Submission failed',
          ...(errorData.errors || {})
        });
        
        if (errorData.errors) {
          const fieldErrors = {};
          Object.entries(errorData.errors).forEach(([field, message]) => {
            fieldErrors[field] = message;
          });
          setErrors(prev => ({ ...prev, ...fieldErrors }));
        }
      } else if (error.request) {
        setErrors({ form: 'Network error - please check your connection' });
      } else {
        setErrors({ form: 'An unexpected error occurred' });
      }
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (!validateStep()) return;
    
    if (step === 1 && !otpSent) {
      sendOtp();
    } else if (step === 1 && otpSent) {
      verifyOtp();
    } else {
      setStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    setStep(prev => prev - 1);
  };

  const stepConfig = [
    { title: 'Phone Verification', desc: "We'll send a verification code to your phone number" },
    { title: 'Business Information', desc: 'Tell us about your business' },
    { title: 'Contact Information', desc: 'How can we reach you?' },
    { title: 'Legal Information', desc: 'Provide your business legal details' },
    { title: 'Address Information', desc: 'Where is your business located?' },
    { title: 'Bank Details', desc: 'Where should we transfer your payments?' },
    { title: 'Account Setup', desc: 'Create your seller account credentials' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-md overflow-hidden p-6">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Seller Registration</h1>
          <p className="mt-2 text-gray-600">
            Complete the form to register as a seller on our platform
          </p>
        </div>
        
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {[1, 2, 3, 4, 5, 6, 7].map((stepNumber) => (
              <div key={stepNumber} className="flex flex-col items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= stepNumber ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'}`}
                  aria-current={step === stepNumber ? 'step' : undefined}
                >
                  {stepNumber}
                </div>
                <div className={`text-xs mt-2 ${step >= stepNumber ? 'text-blue-600 font-medium' : 'text-gray-500'}`}>
                  {stepConfig[stepNumber - 1].title.split(' ')[0]}
                </div>
              </div>
            ))}
          </div>
          <div className="w-full bg-gray-200 h-1 mt-4">
            <div 
              className="bg-blue-600 h-1 transition-all duration-300" 
              style={{ width: `${(step / 7) * 100}%` }}
              aria-label="Progress"
            ></div>
          </div>
        </div>
        
        {/* Error message */}
        {errors.form && (
          <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-md">
            {errors.form}
          </div>
        )}
        
        {/* Current Step Content */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-gray-800">
            {stepConfig[step - 1].title}
          </h2>
          <p className="text-gray-600 mb-4">
            {stepConfig[step - 1].desc}
          </p>
          
          {/* Step 1: Phone Verification */}
          {step === 1 && (
            <>
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number*
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  disabled={otpSent}
                  className={`w-full px-3 py-2 border rounded-md ${errors.phone ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="Enter 10-digit phone number"
                />
               {otp && ( <p className="mt-1 text-sm text-blue-600"> Your OTP is {otp}</p>)}
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                )}
              </div>
              
              {otpSent && (
                <div className="mt-4">
                  <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-1">
                    Verification Code*
                  </label>
                  <input
                    type="text"
                    id="otp"
                    name="otp"
                    value={formData.otp}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md ${errors.otp ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Enter 6-digit OTP"
                  />
                  {errors.otp && (
                    <p className="mt-1 text-sm text-red-600">{errors.otp}</p>
                  )}
                  <p className="mt-2 text-sm text-gray-500">
                    Didn't receive code?{' '}
                    <button
                      type="button"
                      onClick={sendOtp}
                      className="text-blue-600 hover:text-blue-800 focus:outline-none"
                    >
                      Resend OTP
                    </button>
                  </p>
                </div>
              )}
            </>
          )}
          
          {/* Step 2: Business Information */}
          {step === 2 && (
            <div>
              <label htmlFor="businessName" className="block text-sm font-medium text-gray-700 mb-1">
                Business Name*
              </label>
              <input
                type="text"
                id="businessName"
                name="businessName"
                value={formData.businessName}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md ${errors.businessName ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="Your business name"
              />
              {errors.businessName && (
                <p className="mt-1 text-sm text-red-600">{errors.businessName}</p>
              )}
            </div>
          )}
          
          {/* Step 3: Contact Information */}
          {step === 3 && (
            <>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address*
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="your@email.com"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <input
                  type="text"
                  value={formData.phone}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                />
                <p className="mt-1 text-sm text-gray-500">Verified: {formData.phone}</p>
              </div>
            </>
          )}
          
          {/* Step 4: Legal Information */}
          {step === 4 && (
            <>
              <div>
                <label htmlFor="panNumber" className="block text-sm font-medium text-gray-700 mb-1">
                  PAN Number*
                </label>
                <input
                  type="text"
                  id="panNumber"
                  name="panNumber"
                  value={formData.panNumber}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md ${errors.panNumber ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="e.g., ABCDE1234F"
                />
                {errors.panNumber && (
                  <p className="mt-1 text-sm text-red-600">{errors.panNumber}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="gstNumber" className="block text-sm font-medium text-gray-700 mb-1">
                  GST Number*
                </label>
                <input
                  type="text"
                  id="gstNumber"
                  name="gstNumber"
                  value={formData.gstNumber}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md ${errors.gstNumber ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="e.g., 22ABCDE1234F1Z5"
                />
                {errors.gstNumber && (
                  <p className="mt-1 text-sm text-red-600">{errors.gstNumber}</p>
                )}
              </div>
            </>
          )}
          
          {/* Step 5: Address Information */}
          {step === 5 && (
            <>
              <div>
                <label htmlFor="pickupPinCode" className="block text-sm font-medium text-gray-700 mb-1">
                  Pickup Pincode*
                </label>
                <input
                  type="text"
                  id="pickupPinCode"
                  name="pickupPinCode"
                  value={formData.pickupPinCode}
                  onChange={handlePincodeChange}
                  className={`w-full px-3 py-2 border rounded-md ${errors.pickupPinCode ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="6-digit pincode"
                />
                {errors.pickupPinCode && (
                  <p className="mt-1 text-sm text-red-600">{errors.pickupPinCode}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="pickupAddress" className="block text-sm font-medium text-gray-700 mb-1">
                  Pickup Address*
                </label>
                <textarea
                  id="pickupAddress"
                  name="pickupAddress"
                  value={formData.pickupAddress}
                  onChange={handleChange}
                  rows={3}
                  className={`w-full px-3 py-2 border rounded-md ${errors.pickupAddress ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="Full address including street, building, etc."
                />
                {errors.pickupAddress && (
                  <p className="mt-1 text-sm text-red-600">{errors.pickupAddress}</p>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="pickupCity" className="block text-sm font-medium text-gray-700 mb-1">
                    City*
                  </label>
                  <input
                    type="text"
                    id="pickupCity"
                    name="pickupCity"
                    value={formData.pickupCity}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md ${errors.pickupCity ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="City"
                  />
                  {errors.pickupCity && (
                    <p className="mt-1 text-sm text-red-600">{errors.pickupCity}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="pickupState" className="block text-sm font-medium text-gray-700 mb-1">
                    State*
                  </label>
                  <input
                    type="text"
                    id="pickupState"
                    name="pickupState"
                    value={formData.pickupState}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md ${errors.pickupState ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="State"
                  />
                  {errors.pickupState && (
                    <p className="mt-1 text-sm text-red-600">{errors.pickupState}</p>
                  )}
                </div>
              </div>
            </>
          )}
          
          {/* Step 6: Bank Details */}
          {step === 6 && (
            <>
              <div>
                <label htmlFor="accountHolderName" className="block text-sm font-medium text-gray-700 mb-1">
                  Account Holder Name*
                </label>
                <input
                  type="text"
                  id="accountHolderName"
                  name="accountHolderName"
                  value={formData.accountHolderName}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md ${errors.accountHolderName ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="As per bank records"
                />
                {errors.accountHolderName && (
                  <p className="mt-1 text-sm text-red-600">{errors.accountHolderName}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="bankName" className="block text-sm font-medium text-gray-700 mb-1">
                  Bank Name*
                </label>
                <input
                  type="text"
                  id="bankName"
                  name="bankName"
                  value={formData.bankName}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md ${errors.bankName ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="Bank name"
                />
                {errors.bankName && (
                  <p className="mt-1 text-sm text-red-600">{errors.bankName}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="accountNumber" className="block text-sm font-medium text-gray-700 mb-1">
                  Account Number*
                </label>
                <input
                  type="text"
                  id="accountNumber"
                  name="accountNumber"
                  value={formData.accountNumber}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md ${errors.accountNumber ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="9-18 digit account number"
                />
                {errors.accountNumber && (
                  <p className="mt-1 text-sm text-red-600">{errors.accountNumber}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="ifscCode" className="block text-sm font-medium text-gray-700 mb-1">
                  IFSC Code*
                </label>
                <input
                  type="text"
                  id="ifscCode"
                  name="ifscCode"
                  value={formData.ifscCode}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md ${errors.ifscCode ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="11-character IFSC code"
                />
                {errors.ifscCode && (
                  <p className="mt-1 text-sm text-red-600">{errors.ifscCode}</p>
                )}
              </div>
            </>
          )}
          
          {/* Step 7: Account Setup */}
          {step === 7 && (
            <>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password*
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md ${errors.password ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="Create a strong password"
                />
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                )}
                <PasswordStrengthMeter password={formData.password} />
                <p className="mt-1 text-xs text-gray-500">
                  Must contain at least 8 characters with uppercase, lowercase, number, and special character
                </p>
              </div>
              
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password*
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md ${errors.confirmPassword ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="Re-enter your password"
                />
                {errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="documents" className="block text-sm font-medium text-gray-700 mb-1">
                  Documents for Verification*
                </label>
                <input
                  type="file"
                  id="documents"
                  multiple
                  onChange={handleFileChange}
                  className={`w-full px-3 py-2 border rounded-md ${errors.documents ? 'border-red-500' : 'border-gray-300'}`}
                  accept=".pdf,.jpg,.jpeg,.png"
                />
                {errors.documents && (
                  <p className="mt-1 text-sm text-red-600">{errors.documents}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Upload the following documents (Max 5 files, PDF or images up to 5MB each):
                </p>
                <ul className="list-disc pl-5 mt-1 text-xs text-gray-500">
                  <li>PAN Card</li>
                  <li>GST Certificate</li>
                  <li>Bank Account Proof</li>
                  <li>Address Proof</li>
                </ul>
                
                {/* File previews */}
                {filePreviews.length > 0 && (
                  <div className="mt-4 space-y-2">
                    <p className="text-sm font-medium text-gray-700">Selected files:</p>
                    <ul className="space-y-2">
                      {filePreviews.map((file, index) => (
                        <li key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <div className="flex items-center space-x-2">
                            {file.type.startsWith('image/') ? (
                              <img 
                                src={file.url} 
                                alt={file.name} 
                                className="h-10 w-10 object-cover rounded"
                              />
                            ) : (
                              <div className="h-10 w-10 bg-gray-200 flex items-center justify-center rounded">
                                <span className="text-xs">PDF</span>
                              </div>
                            )}
                            <span className="text-sm text-gray-700 truncate max-w-xs">
                              {file.name} ({Math.round(file.size / 1024)} KB)
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            className="text-red-500 hover:text-red-700 focus:outline-none"
                            aria-label={`Remove ${file.name}`}
                          >
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              
              <div className="mt-4 flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="terms"
                    name="terms"
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    className={`focus:ring-blue-500 h-4 w-4 text-blue-600 border rounded ${errors.terms ? 'border-red-500' : 'border-gray-300'}`}
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="terms" className="font-medium text-gray-700">
                    I agree to the{' '}
                    <a href="/terms" className="text-blue-600 hover:text-blue-500">
                      Terms and Conditions
                    </a>{' '}
                    and{' '}
                    <a href="/privacy" className="text-blue-600 hover:text-blue-500">
                      Privacy Policy
                    </a>
                  </label>
                  {errors.terms && (
                    <p className="mt-1 text-sm text-red-600">{errors.terms}</p>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
        
        {/* Navigation buttons */}
        <div className="mt-8 flex justify-between">
          {step > 1 ? (
            <button
              type="button"
              onClick={prevStep}
              disabled={loading}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Back
            </button>
          ) : (
            <div></div>
          )}
          
          {step < 7 ? (
            <button
              type="button"
              onClick={nextStep}
              disabled={loading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? 'Processing...' : 'Next'}
            </button>
          ) : (
            <button
              type="button"
              onClick={submitForm}
              disabled={loading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
            >
              {loading ? 'Submitting...' : 'Submit Registration'}
            </button>
          )}
        </div>
      </div>
      
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full mx-auto overflow-hidden shadow-xl transform transition-all">
            <div className="p-6 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <CheckCircleIcon className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="mt-3 text-lg font-medium text-gray-900">
                Registration Successful!
              </h3>
              <div className="mt-4 text-sm text-gray-500">
                <p className="mb-2">
                  Thank you for registering as a seller. Your request has been submitted successfully.
                </p>
                <p className="mb-4">
                  Our team will review your documents and get back to you within 2-3 business days.
                </p>
                <div className="bg-blue-50 p-3 rounded-md text-left">
                  <h4 className="font-medium text-blue-800 mb-2">Next Steps:</h4>
                  <ul className="space-y-2 text-sm text-blue-700">
                    <li className="flex items-start">
                      <CheckIcon className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                      <span>Check your email for confirmation</span>
                    </li>
                    <li className="flex items-start">
                      <CheckIcon className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                      <span>Prepare your product catalog</span>
                    </li>
                    <li className="flex items-start">
                      <CheckIcon className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                      <span>Complete your profile after approval</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:justify-center">
              <button
                type="button"
                onClick={() => navigate('/home')}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:w-auto sm:text-sm"
              >
                Go to Home
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SellerRegistrationForm;